import type {
  ProposalBasketStoreState,
  BasketProposalPaymentInterval,
  BasketProposal
} from './interfaces/ProposalBasketStoreState';
import { PaymentIntervalId } from './interfaces/ProposalBasketStoreState';
import type { RootState } from './interfaces/RootState';
import Utility from '@/common/UtilityFunctions.js';
import RequestService from '@/common/requestService';
import type { Proposal } from '@/interfaces/proposal';
import { mapToBasketProposalModel } from '@/components/Feature/Basket/utilities/basketUtilities';
import type { MutationTree, ActionTree, Dispatch } from 'vuex';

export const initialState: ProposalBasketStoreState = {
  proposalsInBasket: 0,
  digitalProposalsInBasket: 0,
  manualProposalsInBasket: 0,
  areAllProposalsManual: false,
  updateComponentVsBasket: false,
  selectedProposals: [],
  selectedPaymentIntervals: {},
  proposals: [],
  showRemoveProposalErrorModal: false,
  showRemoveProposalSuccessModal: false,
  index: 0
};

// ================ TRACKING =================

const dispatchInitializeBasketTracking = (dispatch: Dispatch, proposals: Proposal[]) => {
  dispatch(
    'Events/register',
    {
      event: 'checkout-steps',
      id: 'ecommerce',
      vm: {
        actionField: {
          step: 4
        },
        products: proposals.map((proposal) => ({
          name: proposal.name,
          id: proposal.productType,
          price: Utility.getGoogleFriendlyPrice(proposal.price),
          brand: Utility.getBrandIdentifier(),
          quantity: 1,
          channelTypeId: proposal.channelTypeId,
          channelTypeName: proposal.channelTypeName
        }))
      }
    },
    { root: true }
  );
};

const dispatchRemoveProposalTracking = (dispatch: Dispatch, proposalToRemove: BasketProposal) => {
  dispatch(
    'Events/register',
    {
      event: 'product-remove-from-cart',
      id: 'ecommerce',
      vm: {
        products: [
          {
            id: proposalToRemove.productType,
            brand: Utility.getBrandIdentifier(),
            price: new Intl.NumberFormat('en-US').format(proposalToRemove.price),
            quantity: 1
          }
        ]
      }
    },
    { root: true }
  );

  dispatch(
    'Events/register',
    {
      event: 'proposal-basket-remove-proposal',
      id: 'proposalBasketProposal',
      vm: proposalToRemove
    },
    { root: true }
  );
};

const dispatchChangeIntervalTracking = (
  dispatch: Dispatch,
  paymentIntervalId: PaymentIntervalId,
  proposal: BasketProposal
) => {
  let event = '';

  switch (paymentIntervalId) {
    case PaymentIntervalId.Yearly:
      event = 'proposal-basket-select-yearly-interval';
      break;
    case PaymentIntervalId.HalfYearly:
      event = 'proposal-basket-select-halfyearly-interval';
      break;
    case PaymentIntervalId.Quarterly:
      event = 'proposal-basket-select-quarterly-interval';
      break;
    case PaymentIntervalId.Monthly:
      event = 'proposal-basket-select-monthly-interval';
      break;
  }

  dispatch(
    'Events/register',
    {
      event,
      id: 'proposalBasketProposal',
      vm: proposal
    },
    { root: true }
  );
};

const dispatchSelectOrDeselectTracking = (
  dispatch: Dispatch,
  selected: boolean,
  proposal: BasketProposal
) => {
  let event = '';

  if (selected) event = 'proposal-basket-select-proposal';
  else event = 'proposal-basket-deselect-proposal';

  dispatch(
    'Events/register',
    {
      event,
      id: 'proposalBasketProposal',
      vm: proposal
    },
    { root: true }
  );
};

// ================= ACTIONS =================

export const actions: ActionTree<ProposalBasketStoreState, RootState> = {
  async initializeBasket({ commit, dispatch }) {
    await RequestService.get<Proposal[]>(Utility.constants().API.proposal_get_all)
      .then((response) => {
        const proposalDtos = response.data;
        dispatchInitializeBasketTracking(dispatch, proposalDtos);
        const basketProposals = proposalDtos.map(mapToBasketProposalModel);
        commit('setProposals', basketProposals);
        commit(
          'setSelectedProposals',
          basketProposals.map((proposal) => proposal.id)
        );
      })
      .catch((err) => {
        Utility.logError(err, this);
      });
  },
  async setProposals({ commit }) {
    await RequestService.get<Proposal[]>(Utility.constants().API.proposal_get_all).then(
      (response) => {
        const proposalDtos = response.data;
        const basketProposals = proposalDtos.map(mapToBasketProposalModel);
        commit('setProposals', basketProposals);
      }
    );
  },
  async removeProposal({ commit, dispatch }, proposal: BasketProposal) {
    const externalProposalNumber = proposal.externalProposalNumber;

    await RequestService.post<{
      totalPrice: string;
      totalFees: string;
    }>(Utility.constants().API.proposal_delete, JSON.stringify(externalProposalNumber), {
      'Content-Type': 'application/json'
    })
      .then(() => {
        commit('removeProposal', { proposal });
        commit('raiseRemoveSuccess');
        dispatchRemoveProposalTracking(dispatch, proposal);
      })
      .catch(() => {
        commit('raiseRemoveError');
      });
  },
  async selectOrDeselectProposal(
    { commit, state, dispatch },
    { proposal, selected }: { proposal: BasketProposal; selected: boolean }
  ) {
    let selectedProposals: string[] = state.selectedProposals;
    if (selected) selectedProposals.push(proposal.id);
    else
      selectedProposals = state.selectedProposals.filter((chosenPid) => chosenPid !== proposal.id);

    dispatchSelectOrDeselectTracking(dispatch, selected, proposal);
    commit('setSelectedProposals', selectedProposals);
  },
  async changeProposalPaymentInterval(
    { commit, state, dispatch },
    {
      proposalId,
      paymentInterval,
      externalProposalId
    }: {
      proposalId: string;
      paymentInterval: BasketProposalPaymentInterval;
      externalProposalId: string;
    }
  ) {
    const index = state.proposals.findIndex((proposal) => proposal.id === proposalId);
    const extProposalId = externalProposalId?.replace(/\//g, '-');

    await RequestService.get<Proposal[]>(
      `${Utility.constants().API.proposal_update_paymentfrequency}${extProposalId}/${
        paymentInterval.id
      }`
    )
      .then((response) => {
        const proposalDtos = response.data;
        const basketProposals = proposalDtos.map(mapToBasketProposalModel);
        commit('setProposals', basketProposals);
      })
      .catch(() => {
        commit('raiseRemoveError');
      });

    commit('setPaymentInterval', { index, paymentInterval });
    dispatchChangeIntervalTracking(dispatch, paymentInterval.id, state.proposals[index]);
  },
  async dismissRemoveSuccess({ commit }) {
    commit('dismissRemoveSuccess');
  },
  async dismissRemoveError({ commit }) {
    commit('dismissRemoveError');
  }
};

// ================= MUTATIONS =================

export const mutations: MutationTree<ProposalBasketStoreState> = {
  setIndex: (state: ProposalBasketStoreState, index: number) => (state.index = index),
  setProposals: (state: ProposalBasketStoreState, proposals: BasketProposal[]) => {
    proposals.sort((a, b) => a.id.localeCompare(b.id));
    state.proposals = proposals;
    state.proposalsInBasket = proposals.length;
    state.digitalProposalsInBasket = proposals.filter(
      (proposal) => proposal.isDigitallyCreated
    ).length;
    state.manualProposalsInBasket = state.proposalsInBasket - state.digitalProposalsInBasket;
    state.areAllProposalsManual = state.manualProposalsInBasket === state.proposalsInBasket;
    state.updateComponentVsBasket = true;
  },
  setSelectedProposals: (state: ProposalBasketStoreState, selectedProposals: string[]) =>
    (state.selectedProposals = selectedProposals),
  removeProposal: (state: ProposalBasketStoreState, { proposal }: { proposal: BasketProposal }) => {
    state.proposals = state.proposals.filter((p) => p.id !== proposal.id);
    state.selectedProposals = state.selectedProposals.filter((pId) => pId !== proposal.id);
    delete state.selectedPaymentIntervals[proposal.id];
    state.proposalsInBasket = state.proposals.length;
    state.digitalProposalsInBasket = state.proposals.filter(
      (proposal) => proposal.isDigitallyCreated
    ).length;
    state.manualProposalsInBasket = state.proposalsInBasket - state.digitalProposalsInBasket;
    state.areAllProposalsManual = state.manualProposalsInBasket === state.proposalsInBasket;
    state.updateComponentVsBasket = true;
  },
  setPaymentInterval: (
    state: ProposalBasketStoreState,
    { index, paymentInterval }: { index: number; paymentInterval: BasketProposalPaymentInterval }
  ) => {
    const proposal = state.proposals[index];
    proposal.paymentInformation.selectedPaymentInterval = paymentInterval;
    state.selectedPaymentIntervals[proposal.id] = paymentInterval;
  },
  raiseRemoveSuccess: (state: ProposalBasketStoreState) =>
    (state.showRemoveProposalSuccessModal = true),
  dismissRemoveSuccess: (state: ProposalBasketStoreState) =>
    (state.showRemoveProposalSuccessModal = false),
  raiseRemoveError: (state: ProposalBasketStoreState) =>
    (state.showRemoveProposalErrorModal = true),
  dismissRemoveError: (state: ProposalBasketStoreState) =>
    (state.showRemoveProposalErrorModal = false)
};

export default {
  namespaced: true,
  state: initialState,
  actions,
  mutations
};
